import { Http, ResponseError } from '../http';
import { handleAction } from '../actions';
import { getBody } from './browser';

const ERROR_REASON_SUBMITTING = 'submitting';

// Preventing flickering of loaders and submit spamming
const LOADER_HIDE_DELAY = 300;

const FIELD_INVALID_CLASS = 'is-invalid';
const FIELD_SELECTOR = '.form-field';
const FIELD_MESSAGE_SELECTOR = '.form-field-validation';
const FIELD_SUCCESS_MESSAGE_SELECTOR = '.form-field-message';

function createMessageOutletElement(success = false) {
    const message = document.createElement('label');
    message.classList.add(success ? 'form-field-message' : 'form-field-validation');
    message.setAttribute('role', 'alert');

    return message;
}

const formMap = new WeakMap();

class FormException extends Error {
    constructor(message, reason) {
        super(message);
        this._reason = reason;
    }

    get isSubmitting() {
        return this._reason === ERROR_REASON_SUBMITTING;
    }
}

class FormState {
    /**
     * @param {HTMLFormElement} form
     */
    constructor(form) {
        this._form = form;
        this._isSubmitting = false;
        this._needsConfirmation = form.dataset.confirm === 'true';
        this._onSuccessAction = null;
        this._submitFactory = null;
        this._url = this._form.getAttribute('action');
        this._handleActions = Boolean(this._form.dataset.handleActions || true);
    }

    get el() {
        return this._form;
    }

    get url() {
        return this._url;
    }

    get method() {
        return this._form.getAttribute('method') || 'get';
    }

    setSubmitAction(factory) {
        this._submitFactory = factory;

        return this;
    }

    setUrl(value) {
        this._url = value;

        return this;
    }

    async submit(event = undefined, additionalData = undefined, submitter = undefined) {
        if (this._isSubmitting) {
            throw new FormException('Form is already submitting', ERROR_REASON_SUBMITTING);
        }

        this._setSubmitting(true);

        const formSubmitter = submitter || event?.submitter;

        if (formSubmitter && formSubmitter.dataset.success) {
            this._onSuccessAction = JSON.parse(formSubmitter.dataset.success);
        } else if (this._form.dataset.success) {
            this._onSuccessAction = JSON.parse(this._form.dataset.success);
        }

        if (this._needsConfirmation) {
            // TODO implement more universal confirmation async
            // await this._submitWithConfirmation(additionalData);
        } else {
            await this._sendRequest(additionalData);
        }

        this._setSubmitting(false);
    }

    async _sendRequest(additionalData = undefined) {
        const formData = this._collectFormData(this._form);
        const queue = [];

        if (additionalData) {
            Object.keys(additionalData).forEach((key) => {
                if (Array.isArray(additionalData[key])) {
                    additionalData[key].forEach((value) => {
                        formData.append(key, value);
                    });
                } else {
                    formData.append(key, additionalData[key]);
                }
            });
        }

        const submittingEvent = new CustomEvent('submitting', {
            bubbles: true,
            cancelable: false,
            detail: {
                formData: formData,
                queue: queue,
            },
        });

        this._form.dispatchEvent(submittingEvent);

        try {
            if (queue.length) {
                await Promise.all(queue);
            }

            if (this._submitFactory) {
                await this._submitFactory(formData);
            } else {
                let url = this.url;
                const options = {
                    method: this.method,
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                };

                if (this.method.toLowerCase() === 'get') {
                    const searchParams = new URLSearchParams(formData);

                    if (url.includes('?')) {
                        url = `${url}&${searchParams.toString()}`;
                    } else {
                        url = `${url}?${searchParams.toString()}`;
                    }
                } else {
                    options.body = formData;
                }

                const response = await Http.call(url, options);
                this.handleSuccess(response);
            }
        } catch (error) {
            this.handleError(error);
            this._setSubmitting(false);
            throw error;
        }
    }

    handleSuccess(response) {
        this.showMessages(response.data.messages || {}, true);

        const successEvent = new CustomEvent('success', {
            bubbles: false,
            cancelable: true,
            detail: {
                response: response,
            },
        });

        this._form.dispatchEvent(successEvent);
        if (successEvent.defaultPrevented) {
            return;
        }

        if (this._onSuccessAction) {
            handleAction(this._onSuccessAction, this.el);

            return;
        }

        if (this._handleActions && response.data.actions) {
            response.data.actions.forEach(action => {
                handleAction(action, this.el);
            });
        }
    }

    handleError(error) {
        console.error(error);

        if (this._needsConfirmation) {
            // TODO show validation message in confirmation?

            return;
        }

        const errorEvent = new CustomEvent('error', {
            bubbles: false,
            cancelable: true,
            detail: {
                error: error,
            },
        });

        this._form.dispatchEvent(errorEvent);
        if (errorEvent.defaultPrevented) {
            return;
        }

        if (error instanceof ResponseError && error.isValidationError) {
            this.showMessages(error.response.data.errors || {});
        }

        if (this._form.hasAttribute('data-scroll-to-error')) {
            this._form.querySelector(`${FIELD_MESSAGE_SELECTOR}:not(:empty)`)?.parentElement.scrollIntoView();
        }
    }

    showMessages(messages, success = false) {
        this.clearMessages();

        Object.keys(messages).forEach((field) => {
            const formField = this._findDotField(field);

            if (!formField) {
                return;
            }

            const message = Array.isArray(messages[field]) ? messages[field] : [messages[field]];
            let messageOutlet = formField.querySelector(`${FIELD_MESSAGE_SELECTOR}, ${FIELD_SUCCESS_MESSAGE_SELECTOR}`);

            if (!messageOutlet) {
                messageOutlet = createMessageOutletElement(success);
                formField.appendChild(messageOutlet);
            }

            if (message.length === 1) {
                messageOutlet.textContent = message[0];
            } else {
                for (let i = 0; i < message.length; i++) {
                    const span = document.createElement('span');
                    span.classList.add('d-block');
                    span.textContent = message[i];
                    messageOutlet.appendChild(span);
                }
            }

            formField.querySelectorAll('[data-invalid]').forEach((el) => {
                el.classList.add(FIELD_INVALID_CLASS);
            });
        });
    }

    clearMessages() {
        this._form.querySelectorAll(FIELD_SELECTOR).forEach((field) => {
            const output = field.querySelector(`${FIELD_MESSAGE_SELECTOR}, ${FIELD_SUCCESS_MESSAGE_SELECTOR}`);

            if (output) {
                if (output.childElementCount > 0) {
                    output.innerHTML = '';
                } else {
                    output.textContent = '';
                }
            }
        });

        this._form.querySelectorAll('[data-invalid]').forEach((field) => {
            field.classList.remove(FIELD_INVALID_CLASS);
        });
    }

    /**
     * @param {HTMLFormElement} form
     * @returns {FormData}
     * @private
     */
    _collectFormData(form) {
        let formData = new FormData(form);

        // Append checkboxes with unchecked value
        this._form.querySelectorAll('input[name][type="checkbox"]:not(:disabled)').forEach((el) => {
            if (!el.checked && el.dataset.uncheckedValue) {
                formData.append(el.name, el.dataset.uncheckedValue);
            }
        });

        return formData;
    }

    _setSubmitting(value) {
        if (this._isSubmitting === value) {
            return;
        }

        if (value) {
            this._isSubmitting = true;
            this._updateLoaders(true);
        } else {
            setTimeout(() => {
                this._isSubmitting = false;
                this._updateLoaders(false);
            }, LOADER_HIDE_DELAY);
        }
    }

    _updateLoaders(value) {
        const id = this._form.getAttribute('id');
        const loadingTargets = getBody().querySelectorAll(`[data-loader]`);

        loadingTargets.forEach((el) => {
            const targets = el.dataset.loader.split(' ');
            let isTargeted = false;

            if ((id && targets.includes(id)) || targets.includes('all')) {
                isTargeted = true;
            }

            if (!isTargeted) {
                return;
            }

            if (value) {
                el.classList.add('is-loading');
            } else {
                el.classList.remove('is-loading');
            }
        });
    }

    _findDotField(field) {
        const fieldName = field;
        let formField = this._form.querySelector(`${FIELD_SELECTOR}[data-field="${fieldName}"]`);

        if (formField) {
            return formField;
        }

        const bracketPath = convertDotToBracketNotation(fieldName);
        formField = this._form.querySelector(`[name="${bracketPath}"]`)?.closest(FIELD_SELECTOR);

        if (formField) {
            return formField;
        }

        if (!field.match(/\.\d+$/u)) {
            return null;
        }

        return this._findDotField(field.replace(/\.\d+$/u, ''));
    }
}

/**
 * Convert dot notation to bracket notation.
 * e.g. 'user.name' to 'user[name]'
 * @param {string} dotNotation
 * @returns {string}
 */
function convertDotToBracketNotation(dotNotation) {
    if (dotNotation.indexOf('.') === -1) {
        return dotNotation;
    }

    return dotNotation
        .split(/\./u)
        .map((segment, index) => {
            if (index === 0) {
                return segment;
            }

            return `[${segment}]`;
        })
        .join('');
}

/**
 * @returns {FormState|undefined}
 */
function getForm(form) {
    let formEl = form;

    if (typeof form === 'string') {
        formEl = document.getElementById(form);

        if (!formEl) {
            return undefined;
        }
    }

    if (!formMap.has(formEl)) {
        formMap.set(formEl, new FormState(formEl));
    }

    return formMap.get(formEl);
}

function resetFormFields(element) {
    if (!element) return;

    element.querySelectorAll('input, textarea, select').forEach(el => {
        if (el.tagName.toLowerCase() === 'input') {
            if (el.type === 'checkbox' || el.type === 'radio') {
                el.checked = el.defaultChecked;
            } else {
                el.value = el.defaultValue;
            }
        } else if (el.tagName.toLowerCase() === 'textarea' || el.tagName.toLowerCase() === 'select') {
            el.value = el.defaultValue;
        }
    });
}

export {
    convertDotToBracketNotation,
    getForm,
    resetFormFields,
    FormException,
};
